import { CombinedDownload } from '@goldcast/api/content';
import { useMemo, useState, useCallback } from 'react';
import { useIsPostUsingRemotion } from '../SharePopup/PostSharePopup/useIsPostUsingRemotion';
import DownloadsPopupV2Layout from './DownloadsPopupV2Layout';
import { useBrandingToggleVisible } from '../SharePopup/useBrandingToggleVisible';
import useDownloadAsset from '@/hooks/useDownloadAsset';
import { POST_TYPES } from '@/Pages/PostsListPage/constants';
import { isLiveEventRecording, isRemotionMigrationInProgress } from '@/player-config/src/utils/mediaContentUtils';
import { ApiButtonStatus } from '@/components/atoms/ApiButton/ApiButtonTypes';
import type { TextAsset } from '@/Pages/PostsListPage/types';

interface PostDownloadPopupProps {
  loading: boolean;
  processingDownloads: CombinedDownload[];
  recentDownloads: CombinedDownload[];
  selectedPost?: TextAsset | null;
}

export default function PostDownloadPopup({
  loading,
  processingDownloads,
  recentDownloads,
  selectedPost = null
}: Readonly<PostDownloadPopupProps>) {
  const [buttonStatus, setButtonStatus] = useState<ApiButtonStatus>(ApiButtonStatus.IDLE);

  const { isDownloadProcessed, onDownloadTextAsset, updateDownloadState } = useDownloadAsset({
    type: 'TEXT',
    textAsset: selectedPost
  });

  const isMultiModal = useMemo(() => {
    const { multi_modal_enabled } = selectedPost?.content ?? {};
    const { type, clip_assets } = selectedPost?.asset_metadata ?? { clip_assets: [] };

    return multi_modal_enabled && (type === POST_TYPES.BLOG || clip_assets?.length > 0);
  }, [selectedPost]);

  const isBrandingToggleVisible = useBrandingToggleVisible();

  const showBrandingToggle = useMemo(() => {
    const { type } = selectedPost?.asset_metadata ?? {};
    return isBrandingToggleVisible && (type === POST_TYPES.BLOG || type === 'LINKEDIN');
  }, [selectedPost, isBrandingToggleVisible]);

  const showMigrationInProgressUI = useMemo(() => {
    if (!selectedPost?.content) {
      return false;
    }
    const { media_source_type, event_type, broadcast_type } = selectedPost.content;
    if (!media_source_type || !event_type || !broadcast_type) {
      return false;
    }
    return (
      isLiveEventRecording(media_source_type, event_type, broadcast_type) &&
      isRemotionMigrationInProgress(selectedPost.content)
    );
  }, [selectedPost]);

  const { playerConfigExists } = useIsPostUsingRemotion(selectedPost);

  const { isDownloadDisabled, statusMessage } = useMemo(() => {
    if (!selectedPost) {
      return { isDownloadDisabled: true, statusMessage: 'No post selected' };
    }

    let isDownloadDisabled = showMigrationInProgressUI && isMultiModal;
    let statusMessage: string | null = null;

    if (playerConfigExists === false) {
      isDownloadDisabled = true;
    }

    if (isDownloadDisabled) {
      if (showMigrationInProgressUI && isMultiModal) {
        statusMessage = 'Migration in progress';
      }
    }

    if (buttonStatus === ApiButtonStatus.IN_PROGRESS) {
      statusMessage = 'Initiating Download..';
    } else if (buttonStatus === ApiButtonStatus.COMPLETED && isDownloadProcessed) {
      statusMessage = 'Download started';
    } else if (buttonStatus === ApiButtonStatus.FAILED) {
      statusMessage = 'Download failed';
    }

    return {
      isDownloadDisabled,
      statusMessage
    };
  }, [selectedPost, showMigrationInProgressUI, isMultiModal, playerConfigExists, buttonStatus, isDownloadProcessed]);

  const handleStatusChange = useCallback(
    (status: ApiButtonStatus) => {
      setButtonStatus(status);
      updateDownloadState(status);
    },
    [updateDownloadState]
  );

  const handleDownloadClick = useCallback(async (): Promise<void> => {
    handleStatusChange(ApiButtonStatus.IN_PROGRESS);
    try {
      await onDownloadTextAsset();
      handleStatusChange(ApiButtonStatus.COMPLETED);
    } catch {
      handleStatusChange(ApiButtonStatus.FAILED);
    }
  }, [onDownloadTextAsset, handleStatusChange]);

  return (
    <DownloadsPopupV2Layout
      loading={loading}
      processingDownloads={processingDownloads}
      recentDownloads={recentDownloads}
      downloadAction={handleDownloadClick}
      statusMessage={statusMessage}
      isDownloadDisabled={isDownloadDisabled ?? false}
      showBrandingToggle={showBrandingToggle}
    />
  );
}
